import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { saveCollection, getCollection } from '../../services/api/Collection';
import { ICollection } from '../../services/types';
import useBackdrop from '../useBackdrop';
import useSnackbar from '../useSnackbar';

enum QueryKey {
  Collection = 'Collection',
}

interface IUseCollectionMutation {
  createOrUpdate: UseMutationResult<Partial<ICollection>, Error, Partial<ICollection>>;
}

export const useCollection = (trackingId?: string): UseQueryResult<ICollection | undefined, AxiosError> =>
  useQuery<ICollection | undefined, AxiosError>(
    [QueryKey.Collection, trackingId],
    async () => {
      if (!trackingId) {
        return;
      }

      const [fetchPromise] = getCollection(trackingId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
    },
  );

export const useCollectionMutation = (): IUseCollectionMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const createOrUpdate = useMutation<Partial<ICollection>, Error, Partial<ICollection>>(
    async (collection: Partial<ICollection>) => {
      try {
        backdrop.show();
        const result = await saveCollection(collection);

        return result.data;
      } catch (e) {
        snackbar.error(e);
        throw e;
      } finally {
        backdrop.hide();
      }
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Saved successfully.');
        queryClient.invalidateQueries(QueryKey.Collection);
        queryClient.setQueryData([QueryKey.Collection, variables.trackingId], data);
      },
      onError: e => {
        snackbar.error(e.message);
      },
    },
  );

  return {
    createOrUpdate,
  };
};
